import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLazyGetRedemptionHistoryQuery } from "../../../services/apiSlice";
import { convertUTCToLocalString } from "../../../Utils/helper";
import RedemptionHistoryCard from "./RedemptionHistoryCard";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import FooterContact from "../../../components/Footer/FooterContact";
import Breadcrumb from "../../Profile/ProfileCommon/Breadcrumb";

const RedemptionHistory = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [getRedemptionHistory, { isFetching }] =
    useLazyGetRedemptionHistoryQuery();

  const [redemptionList, setRedemptionList] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const observerRef = useRef(null);
  const lastFetchPageRef = useRef(0);
  const LIMIT = 5;

  const formatRewardTitle = useCallback((reward) => {
    if (!reward) return "Unknown Reward";

    const { type, name, value, reward_quantity } = reward;
    const numericValue = Number(value || 0);
    const quantity = reward_quantity || 1;

    switch (type) {
      case "COIN":
        const unit = numericValue > 1 ? "Coins" : "Coin";
        return `${numericValue} ${unit}`;

      case "XP":
        return `${numericValue} XP`;

      case "ITEM":
      case "DISCOUNT":
        return `${quantity} x ${name}`;

      default:
        return `${quantity} x ${name}`;
    }
  }, []);

  const fetchRedemptions = useCallback(
    async (currentPage) => {
      if (
        isFetching ||
        !hasMore ||
        isLoading ||
        currentPage <= lastFetchPageRef.current
      ) {
        return;
      }

      setIsLoading(true);
      try {
        const response = await getRedemptionHistory({
          page: currentPage,
          limit: LIMIT,
        }).unwrap();

        const newRedemptions = response?.data || [];

        setRedemptionList((prev) => {
          const existingIds = new Set(prev.map((r) => r.id));
          const uniqueNew = newRedemptions.filter(
            (r) => !existingIds.has(r.id),
          );
          return [...prev, ...uniqueNew];
        });

        if (newRedemptions.length < LIMIT) {
          setHasMore(false);
        }
        lastFetchPageRef.current = currentPage;
      } catch (err) {
        console.error("Failed to fetch redemptions:", err);
        setHasMore(false);
      } finally {
        setIsLoading(false);
      }
    },
    [getRedemptionHistory, isFetching, hasMore, isLoading],
  );

  useEffect(() => {
    if (page === 1 && !isFetching && !isLoading) {
      fetchRedemptions(1);
    }
  }, [fetchRedemptions, isFetching, page, isLoading]);

  useEffect(() => {
    if (page > 1) {
      fetchRedemptions(page);
    }
  }, [page, fetchRedemptions]);

  const lastElementRef = useCallback(
    (node) => {
      if (!node || isFetching || !hasMore || isLoading) return;
      if (observerRef.current) observerRef.current.disconnect();

      let debounceTimeout;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(() => {
              setPage((prev) => prev + 1);
            }, 300);
          }
        },
        { threshold: 0.1 },
      );
      observerRef.current.observe(node);
    },
    [isFetching, hasMore, isLoading],
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  const renderList = () => {
    if (redemptionList.length === 0) {
      return (
        <div className="text-center text-gray-500 py-4">
          {t("stickers.noRedemptions")}
        </div>
      );
    }

    return redemptionList.map((item, index) => {
      const isLastItem = index === redemptionList.length - 1;
      const isRedeemed = item.type === "REDEEMED";

      const stickerName = item.sticker?.name || "Stickers";
      const displayImage = item.sticker?.image?.file_path;

      let title = "";
      let subTitle = "";

      if (isRedeemed) {
        title = `${t(
          "stickers.exchangedFor",
        )} ${formatRewardTitle(item.reward)}`;
        subTitle = `- ${item.quantity} ${stickerName}`;
      } else {
        const questName = item.quest_name || "Quest";
        title = `${t("stickers.completed")} ${questName}`;
        subTitle = `+ ${item.quantity} ${stickerName}`;
      }

      return (
        <div
          key={item.id || index}
          ref={isLastItem ? lastElementRef : undefined}
          className="mb-4"
        >
          <RedemptionHistoryCard
            title={title}
            subTitle={subTitle}
            timestamp={convertUTCToLocalString(item.created_at)}
            imagePath={displayImage}
            isNegative={isRedeemed}
          />
        </div>
      );
    });
  };

  return (
    <div className="flex flex-col w-full max-w-[480px] h-full bg-white overflow-y-auto">
      <LoadingCommon statusLoading={isFetching || isLoading} />

      <div className="flex flex-col flex-1 px-[24px] pb-[120px]">
        <Breadcrumb
          className="mb-6"
          breadCrumbTiltle={t("stickers.redemptionHistory")}
          onClickBack={() =>
            navigate("/stickers", {
              state: { direction: -1 },
              replace: false,
            })
          }
        />

        {renderList()}
      </div>

      <FooterContact className={"!bg-white"} />
    </div>
  );
};

export default RedemptionHistory;
